import { useReducer, useEffect, useContext, useRef, useCallback } from 'react'
import {
    UniversalModal,
    UniversalDataTable,
    UniversalLoadingCircle,
    UniversalToolBar,
    Grid,
} from '../../../UniversalComponents'
import {
    Delete as DeleteIcon,
    Archive as ArchiveIcon,
    ArrowOutward as ArrowOutwardIcon,
    AddBox as AddBoxIcon,
    Restore,
    DeleteForever,
    DirectionsBoat,
    Star as StarIcon,
} from '@mui/icons-material'
import {
    columnMapper,
    handleActivation,
    handleDeletion,
    standardColumnMapper,
    permissionsCheck,
    useDebounce,
} from '../../../../utilities/functionTools'

import {
    getBoats,
    deactivateBoats,
    deleteBoat,
    getBoatsWithLimiter,
} from '../../../../apiRoutes'
import {
    useAuth,
    usePermissions,
} from '../../../../context/AuthenticationContext'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { useSocket } from '../../../../context/SocketContext'
import BoatConfigurationForm from './BoatConfigForms/BoatConfigurationForm'
import BoatConfigurationInsights from './BoatConfigInsights'
import EngineeringIcon from '@mui/icons-material/Engineering'
import { useGlobalState } from '../../../../context/GlobalStateContext'
import { Box, Tab, Tabs } from '@mui/material'

const today = new Date().toISOString().substring(0, 10)

const BoatManagement = () => {
    const defaultColumnVisibility = {
        nickname: true,
        shortHin: true,
        fullHin: true,
        deviceSerial: true,
        dealerName: true,
        dealerVisibility: true,
        gpsEnabled: true,
    }
    const setToastMessage = useContext(ToastMessage)
    const authentication = useAuth()

    const { setUser } = useAuth()
    const { setMinimized, restoredData } = useMinimizer()
    const { auth, setAuth } = authentication
    const currentUser = useAuth()
    const { userProfile } = currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile
    const { username, activeManufacturer, userPreferences, _id } = auth
    const boatManagementTable = auth?.userPreferences?.boatManagementTable
        ? auth?.userPreferences?.boatManagementTable
        : []
    const defaultState = {
        open: false,
        row: {
            gpsEnabled: false,
            manufacturer: { manufacturerName: activeManufacturer },
        },
        columnVisibility:
            Object?.keys(boatManagementTable).length !== 0
                ? boatManagementTable
                : defaultColumnVisibility,
        reset: false,
        openBoatInsights: false,
        formCache: {},
        archive: [],
        viewArchived: false,
        filteredBoats: [],
        viewDeactivated: false,
        activateData: [],
        data: [],
        userBoats: [],
        boats: {
            boatConfig: {
                columns: [
                    {
                        field: 'fullHin',
                        headerName: 'HIN',
                        width: 150,
                        type: 'string',
                        filterable: true,
                        searchable: true,
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'nickname',
                        headerName: 'BOAT NICKNAME',
                        width: 150,
                        type: 'string',
                        filterable: true,
                        searchable: true,
                    },
                    {
                        field: 'modelYear',
                        headerName: 'MODEL YEAR',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'model',
                        headerName: 'MODEL',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'deviceSerial',
                        headerName: 'DEVICE SERIAL',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'iccid',
                        headerName: 'SIM ID',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'engineManufacturer',
                        headerName: 'ENGINE MANUFACTURER',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'engineModel',
                        headerName: 'ENGINE MODEL',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'engineSerial',
                        headerName: 'ENGINE SERIAL',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'dealerName',
                        headerName: 'DEALER NAME',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'userEmail',
                        headerName: 'OWNER EMAIL',
                        width: 150,
                        type: 'string',
                        filterable: false,
                        searchable: false,
                    },
                    {
                        field: 'gpsEnabled',
                        headerName: 'GPS ENABLED',
                        width: 300,
                        type: 'boolean',
                        filterable: false,
                        searchable: false,
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.boats?.boatConfig?.density ||
            'standard',
        paginationModel: { page: 0, pageSize: 50 },
        rowCount: 0,
        userBoatTotal: 0,
        loading: false,
        apiSearch: '',
        noResults: false,
        activeTab: 0,
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const userPermissions = usePermissions()

    const {
        open,
        row,
        columnVisibility,
        openBoatInsights,
        formCache,
        archive,
        viewArchived,
        filteredBoats,
        viewDeactivated,
        data,
        activateData,
        boats,
        density,
        paginationModel,
        rowCount,
        userBoatTotal,
        loading,
        apiSearch,
        noResults,
        activeTab,
        userBoats,
    } = state

    const { boatConfig } = boats

    const interval = useRef<any>(10000)
    const { apiSocket } = useSocket()

    const { globalState } = useGlobalState()
    const { oupUser } = globalState

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const tabViews = [
        {
            label: 'All Boats',
            columns: boats?.boatConfig?.columns,
            data: data,
            // icon: <DirectionsBoat />,
        },
        {
            label: 'Your Assigned Boats',
            columns: boats?.boatConfig?.columns,
            data: userBoats,
            icon: <StarIcon />,
        },
    ]

    const currentData = tabViews[activeTab]

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active) => {
        setState({ loading: true })
        let boats: any
        try {
            const data = await getBoatsWithLimiter({
                active,
                page: paginationModel?.page + 1,
                limit: 50,
                filter: debouncedSearchQuery
                    ? JSON.stringify(debouncedSearchQuery)
                    : undefined,
            })
            boats = Array.isArray(data?.boats) ? data?.boats : []
            const total = Number(data?.total) || 0
            const userBoatTotal = Number(data?.userBoats?.length) || 0

            if (
                boats?.length === 0 &&
                total === 0 &&
                debouncedSearchQuery !== null
            ) {
                setToastMessage(
                    'No boats found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: total,
                    userBoatTotal: userBoatTotal,
                    loading: false,
                    noResults: true,
                })
                return
            }

            boats = columnMapper(defaultColumns, data?.boats).map(
                (boat: any) => ({
                    ...boat,
                    id: boat?._id,
                })
            )
            const userBoats = columnMapper(defaultColumns, data?.userBoats).map(
                (boat: any) => ({
                    ...boat,
                    id: boat?._id,
                })
            )

            setState({
                data: boats,
                userBoats: userBoats,
                userBoatTotal: userBoatTotal,
                rowCount: total,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }

            setState({ data: [], rowCount: 0 })
            setToastMessage(errorMessage, 'error')
            console.error(err)
        }
    }

    const collectColumnData = () => {
        const boats =
            userPreferences?.dataTableColumns?.boats?.boatConfig?.columns

        if (Array.isArray(boats)) {
            const mapColumns = boats.map((boat) => ({
                field: boat?.field,
                headerName: boat?.headerName,
                width: boat?.width,
                isVisible: boat.isVisible,
                type: boat?.type,
            }))

            const format = {
                boatConfig: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, boats: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'boats',
                value: boats,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivateData = () => {
        handleActivation({
            activateData,
            data,
            returnKey: 'fullHin',
            setState,
            getData,
            route: deactivateBoats,
            viewDeactivated,
            setToastMessage,
            page: 'boats',
        })
    }

    const handleDeletionData = () => {
        handleDeletion({
            activateData,
            data,
            returnKey: 'fullHin',
            setState,
            getData,
            route: deleteBoat,
            viewDeactivated,
            setToastMessage,
            page: 'boats',
        })
    }

    const handleDeletionCheck = () => {
        setToastMessage(
            `Are you sure you want to delete these Boats?`,
            'warning',
            handleDeletionData
        )
    }

    const setDeactivatedView = () => {
        setState({ viewDeactivated: !viewDeactivated })
    }

    useEffect(() => {
        collectColumnData()
        getData(true)
        apiSocket?.on('boat-configuration', (newData) => {
            if (newData) {
                getData(true)
                setToastMessage(`Boat configuration has just updated.`)
            }
        })
    }, [])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    useEffect(() => {
        setState({ paginationModel: { page: 0, pageSize: 50 } })
        getData(!viewDeactivated)
    }, [viewDeactivated, activeManufacturer])

    useEffect(() => {
        if (restoredData?.id !== undefined) {
            setState({ row: restoredData, open: true })
            setMinimized(false, restoredData.id, true)
        }
    }, [restoredData])

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.fullHin) {
            setState({ row: tableRow, open: !open })
        } else {
            setState({
                row: defaultState.row,
                open: !open,
            })
        }
        if (newData !== undefined) {
            return getData(true)
        }
    }

    const minimizeModal = () => {
        setMinimized(true, {
            title: formCache?.fullHin
                ? `Edit: ${formCache.fullHin}`
                : 'Configure a Boat',
            link: '/admin/boats',
            data: formCache.id
                ? { ...formCache, tab: 'configuration' }
                : {
                      ...formCache,
                      id: 'newConfiguration',
                      tab: 'configuration',
                  },
            id: formCache.id ? formCache.id : 'newConfiguration',
        })
        setState({ open: false })
    }

    const setColumnHeaders = (columnHeader, reset?: boolean) => {
        if (!reset) {
            setState({
                columnVisibility: {
                    ...defaultColumnVisibility,
                    ...boatManagementTable,
                    ...columnHeader,
                },
            })
            setAuth({
                preferences: {
                    boatManagementTable: {
                        ...defaultColumnVisibility,
                        ...boatManagementTable,
                        ...columnHeader,
                    },
                },
            })
        } else {
            setAuth({
                preferences: {
                    boatManagementTable: defaultColumnVisibility,
                },
            })
        }
    }

    const defaultColumns = [
        'fullHin',
        'manufacturer.manufacturerName',
        'nickname',
        'boatModel.modelYear',
        'boatModel.model',
        'device.deviceSerial',
        'device.iccid',
        'engineModel',
        'engineSerial',
        'engineManufacturer',
        'dealer.dealerName',
        'owner.userEmail',
        'gpsEnabled',
        'engine.engineModel.engineModelYear',
        'device._id',
        'engine._id',
        'dealer._id',
        'boatModel._id',
        'createdAt',
        'productionStartDate',
        'shipDate',
        'deliveryDate',
        'dealerVisibility',
        'downloadFileMatchFlag',
        'shortHin',
        'archived',
        'updatedBy',
        'updatedAt',
        'engine.engineIdentifier',
        'engine.engineName',
        'tags',
    ]

    const buttons = [
        {
            buttonName: 'Add New Boat Configuration',
            buttonIcon: <EngineeringIcon />,
            buttonFunction: setModalVisbility,
            disabled: permissionsCheck(userProfilePermissions, 'Boats'),
        },

        {
            buttonName: 'Reset Table',
            buttonIcon: <Restore />,
            buttonFunction: () => {
                setState({ columnVisibility: defaultColumnVisibility })
                setColumnHeaders(defaultColumnVisibility, true)
            },
            disabled: permissionsCheck(userProfilePermissions, 'Boats'),
        },

        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivateData,
            disabled: permissionsCheck(userProfilePermissions, 'Boats'),
        },
        {
            buttonName: viewDeactivated
                ? 'View Active Boats'
                : 'View Deactivated Boats',
            buttonIcon: <Restore />,
            buttonFunction: setDeactivatedView,
        },
        {
            buttonName: 'Delete',
            buttonIcon: <DeleteForever />,
            buttonFunction: handleDeletionCheck,
            disabled: superAdmin && superAdmin === true ? false : true,
            display: !viewDeactivated ? 'none' : null,
        },
        // {
        //     buttonName: 'Export',
        //     buttonIcon: <ArrowOutwardIcon />,
        //     buttonFunction: () => {},
        // },
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
            type: col.type,
        }))

        const setDensity = state.density

        const setData = { boatConfig: { columns: format, density: setDensity } }

        setState({
            boats: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            boats: {
                boatConfig: {
                    ...boatConfig,
                    density: newDensity,
                },
            },
        })
    }

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
                <Box
                    sx={{
                        display: 'flex',
                        border: '1px solid',
                        borderColor: 'divider',
                        marginTop: 2.5,
                        width: 'auto',
                        maxWidth: 500,
                        minWidth: 140,
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) =>
                                setState({ activeTab: newValue })
                            }
                            variant="fullWidth"
                            scrollButtons="auto"
                            sx={{
                                height: '100%',

                                '& .MuiTabs-flexContainer': {
                                    height: '100%',
                                },
                                '& .MuiTab-root': {
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                    px: 3,
                                    py: 2,
                                    minHeight: 52,
                                    fontSize: '0.95rem',
                                    padding: 2,

                                    color: 'text.primary',

                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                        color: 'text.primary',
                                    },
                                },
                                '& .Mui-selected': {
                                    bgcolor: 'action.selected',
                                    fontWeight: 600,
                                    color: 'primary.main',
                                },
                                '& .MuiTabs-indicator': {
                                    left: 0,
                                    width: 4,
                                    bgcolor: 'primary.main',
                                },
                            }}
                        >
                            {tabViews?.map((view, i) => (
                                <Tab
                                    key={i}
                                    label={view?.label}
                                    icon={view?.icon}
                                    iconPosition="start"
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <UniversalLoadingCircle
                    data={currentData?.data ?? []}
                    reloadFunction={!viewDeactivated ? getData : () => {}}
                    customTimeoutMessage={
                        !!viewDeactivated && 'No Deactivated Boats Available'
                    }
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <UniversalDataTable
                            data={currentData?.data ?? []}
                            apiDensity={density}
                            apiColumns={boats?.boatConfig?.columns}
                            savePreferences={() => submitColumnData()}
                            visibleColumns={[]}
                            getRowData={(tableRow) =>
                                // setModalVisbility(tableRow, undefined)
                                setState({
                                    openBoatInsights: true,
                                    row: tableRow,
                                })
                            }
                            getColumnHeaderVisibility={(columnHeader) =>
                                setColumnHeaders(columnHeader)
                            }
                            columnsAvailable={columnVisibility}
                            onRowSelectionModelChange={(newSelection) => {
                                setState({
                                    activateData: newSelection,
                                })
                            }}
                            pinnedColumns={{
                                left: ['fullHin'],
                                right: [''],
                            }}
                            onUpdatedColumnsChange={handleUpdatedColumnsChange}
                            onDensityChange={handleDensityChange}
                            rowCount={
                                activeTab === 0 ? rowCount : userBoatTotal
                            }
                            paginationModel={paginationModel}
                            loading={loading}
                            paginationMode="server"
                            filterMode="server"
                            sortingMode="server"
                            setPaginationModel={(model) => {
                                setState({ paginationModel: model })
                            }}
                            pageSizeOptions={[50]}
                            quickSearchFields={['fullHin', 'nickname']}
                            onApiSearch={(text) => {
                                setState({ noResults: false })
                                onApiSearch(text)
                            }}
                        />
                    </Box>
                </UniversalLoadingCircle>
            </Grid>
            <UniversalModal
                title={'Configure a Boat'}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                timeStamp={`${
                    row.fullHin
                        ? ` ${
                              row.updatedBy
                                  ? ` Modified By ${row.updatedBy}`
                                  : ''
                          } ${
                              row.updatedAt
                                  ? `| Modified Date ${row.updatedAt.substring(
                                        0,
                                        10
                                    )}`
                                  : ''
                          }  `
                        : ` Modified By: ${username} | Modified Date: ${today} `
                }`}
                onMinimize={minimizeModal}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 10,
                    marginBottom: 5,
                }}
                draggable={true}
                minimizable={true}
            >
                <BoatConfigurationForm
                    row={row}
                    superAdmin={superAdmin}
                    formDescription="Enter Boat Details To Add A New Boat."
                    onClose={(tableRow: any, newData?: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                    saveCache={(data: any) => setState({ formCache: data })}
                    permissionCheck={permissionsCheck(
                        userProfilePermissions,
                        'Boats'
                    )}
                />
            </UniversalModal>
            {
                /// Important to be conditionally formatted for mounting
            }
            {openBoatInsights === true && (
                <UniversalModal
                    title={`${row.fullHin} Live Data Insights`}
                    open={openBoatInsights}
                    onClose={() => {
                        setState({
                            openBoatInsights: false,
                            row: defaultState.row,
                        })
                        clearInterval(interval.current)
                        interval.current = undefined
                        return interval.current
                    }}
                    wrapperStyle={{
                        margin: 'auto',
                        width: '90%',
                        marginTop: 10,
                        marginBottom: 5,
                    }}
                >
                    <BoatConfigurationInsights
                        row={row}
                        openEdit={() =>
                            setState({
                                open: true,
                                openBoatInsights: false,
                            })
                        }
                        interval={interval}
                        editBoat={true}
                    />
                </UniversalModal>
            )}
        </Grid>
    )
}
export default BoatManagement
